import styles from './index.module.css'

import cn from 'clsx'
import get from 'lodash/get'
import React, { useEffect, useMemo, useState } from 'react'
import type { Location } from 'react-router-dom'

import type { BusinessData } from 'types/business'
import type { AddToDiscoUser, LoginDetails } from 'types/user'

import { Flex, Heading, Link, Spinner, Text } from 'components/ds'

import ForgotPasswordForm from './forgot-password-form'
import { usePromiseAsString } from './hooks'
import Instruction from './instruction'
import PlainLoginForm from './plain-login-form'
import SamlDisabled from './saml-disabled'
import type { SamlDiscoveryPayload } from './saml-discovery-form'
import SamlDiscoveryForm from './saml-discovery-form'
import SamlForm from './saml-form'
import SignUpForm from './sign-up-form'
import type {
  InitialIntent,
  LoginFormState,
  LoginFormType,
  SignUpDetails,
} from './types'
import Warning from './warning'

export type Props = {
  // The current user
  user: AddToDiscoUser
  // Initial Email for the Sign Up form
  initialEmail?: string
  // The current location
  location: Location
  // The heading/title of the login form
  heading?: string
  // A message to display to the user
  message?: string
  // Initial Intent to show when launching
  initialIntent?: InitialIntent
  // The login function to call when the user submits the form
  onLoginUser: (
    loginDetails: LoginDetails,
    returnPath: string | null | undefined,
  ) => unknown
  // maybe remove returnPath and handle externally
  // The auto-login function to call
  onAutoLoginUser?: (returnPath: string) => void
  // maybe remove returnPath and handle externally
  // Forgot password function to call
  onForgotPassword: (
    email: string,
    returnUrl: string,
  ) => Promise<{
    success: boolean
  }>
  // Sign up user function to call
  onSignupCatalogUser?: (details: SignUpDetails) => Promise<{
    status: string
  }>
  // Supplying this will enable SAML discovery login
  onSamlDiscovery?: (email: string) => Promise<SamlDiscoveryPayload>
  // The signup URL to use, create catalog account
  onGetSignupUrl?: () => Promise<string>
  // Supplying this will let the login form detect the business login mechanism.
  onGetBusiness?: () => Promise<Pick<BusinessData, 'login_mechanism'>>
  // Optional function to call when the heading is clicked
  onHeadingClick?: () => void
  // Optional class name to pass through
  className?: string
}

/**
 * Comprehensive login form component supporting multiple authentication methods
 * Handles plain login, SAML authentication, signup, and password recovery
 * @param user - Current user object
 * @param initialEmail - Pre-filled email for signup form
 * @param location - Current location object with query parameters
 * @param heading - Form title/heading text
 * @param message - Optional message to display to user
 * @param initialIntent - Initial form state ('sign_in', 'sign_up', 'forgot_password', 'saml')
 * @param onLoginUser - Login submission handler
 * @param onForgotPassword - Password reset handler
 * @param onAutoLoginUser - Auto-login handler
 * @param onSignupCatalogUser - Signup handler for catalog users
 * @param onSamlDiscovery - SAML discovery handler
 * @param onGetSignupUrl - Signup URL provider
 * @param onGetBusiness - Business data provider
 * @param onHeadingClick - Heading click handler
 * @param className - Additional CSS classes
 */
const LoginForm = ({
  user,
  initialEmail,
  location,
  heading = 'Sign in to DISCO',
  message,
  initialIntent = 'sign_in',
  onLoginUser,
  onForgotPassword,
  onAutoLoginUser,
  onSignupCatalogUser,
  onSamlDiscovery,
  onGetSignupUrl,
  onGetBusiness,
  onHeadingClick,
  className,
}: Props): React.ReactNode => {
  // The state of the login form, which can be 'loading', 'ready' etc.
  const [state, setState] = useState<LoginFormState>('init')
  // The type of login form to show, 'plain' or 'saml' are the only options
  const [type, setType] = useState<LoginFormType>('plain')
  // The number of login attempts
  const [loginAttempts, setLoginAttempts] = useState(0)
  // If SAML is enabled
  const [samlEnabled, setSamlEnabled] = useState(false)
  // Show the SAML disabled form
  const [showSamlDisabledForm, setShowSamlDisabledForm] = useState(false)
  // Get the signup and forgot password URLs if promises are provided
  const signupUrl = usePromiseAsString(onGetSignupUrl, () => setState('error'))

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  )

  // Pass through the login details to the provided login function
  const handleLogin = (loginDetails: LoginDetails) => {
    onLoginUser(
      loginDetails,
      decodeURIComponent(
        searchParams.get('return') || searchParams.get('next') || '',
      ),
    )
    setLoginAttempts(v => v + 1)
  }

  const handleForgotPassword = (email: string) => {
    const next = searchParams.get('return') || searchParams.get('next') || ''
    return onForgotPassword(email, next)
  }

  const goToSaml = () => {
    if (onGetBusiness) {
      setType('saml')
    } else {
      setType('saml_discovery')
    }
  }

  // Get the business login mechanism if getBusiness is provided
  useEffect(() => {
    if (state !== 'init') return

    const initBusiness = async () => {
      try {
        let newState: LoginFormState = 'ready'
        let newType: LoginFormType = 'plain'

        // Get the login mechanism if getBusiness is provided
        if (onGetBusiness) {
          setState('loading')
          const business = await onGetBusiness()
          newType = business.login_mechanism || 'plain'
          setSamlEnabled(newType === 'saml')
        }

        // Try and set the state to the initial intent
        if (initialIntent === 'sign_up' && onSignupCatalogUser) {
          newState = 'sign_up'
        } else if (initialIntent === 'forgot_password' && onForgotPassword) {
          newState = 'forgot_password'
        } else if (initialIntent === 'saml') {
          if (onSamlDiscovery) {
            newType = 'saml_discovery'
          }
        }

        // Set the type and state to the initial intent
        setType(newType)
        setState(newState)
      } catch {
        setState('error')
      }
    }

    initBusiness()
  }, [
    onGetBusiness,
    initialIntent,
    state,
    onSignupCatalogUser,
    onForgotPassword,
    onSamlDiscovery,
  ])
  // Attempt to auto-login the user
  useEffect(() => {
    if (state !== 'init') return

    const autoLogin = async () => {
      if (searchParams.get('reason') === 'user_not_in_business') {
        setState('user_not_in_business')
        return
      }

      if (onAutoLoginUser && searchParams.get('next')) {
        const next =
          searchParams.get('return') || searchParams.get('next') || ''
        await onAutoLoginUser(next)
      }
    }

    autoLogin()
  }, [
    onAutoLoginUser,
    searchParams.get('next'),
    searchParams.get('reason'),
    searchParams.get('return'),
    state,
  ])
  // Grab the user's error code
  const errorCode: string | null = get(user.error, 'code', null)
  const isTransitioning = user.loggedIn && !user.preloaded
  return (
    <Flex
      width="100%"
      direction="column"
      align="center"
      className={cn(className, styles.Wrapper)}
      data-dont-close-formats-dropdown
    >
      <Flex
        direction="column"
        minWidth="230px"
        maxWidth="400px"
        width="100%"
        gap="2"
      >
        {state === 'error' ? (
          <Warning>
            Sorry! DISCO is unavailable!
            <br />
            Please refresh page or{' '}
            <a href="mailto:support@disco.ac">contact support</a>.
          </Warning>
        ) : state === 'init' || state === 'loading' ? (
          <Flex
            align="center"
            justify="center"
            gap="2"
            className={styles.DelayedAppear}
          >
            <Spinner />
            <Text align="center" color="gray">
              Loading...
            </Text>
          </Flex>
        ) : state === 'user_not_in_business' ? (
          <Instruction text="Please check your email for access link." />
        ) : state === 'forgot_password' ? (
          <ForgotPasswordForm
            onSubmit={handleForgotPassword}
            onDismiss={() => setState('ready')}
          />
        ) : state === 'sign_up' && onSignupCatalogUser ? (
          <SignUpForm
            initialEmail={initialEmail}
            onSignupUser={onSignupCatalogUser}
            onBackToLogin={() => setState('ready')}
          />
        ) : showSamlDisabledForm ? (
          <SamlDisabled onDismiss={() => setShowSamlDisabledForm(false)} />
        ) : type === 'plain' ? (
          <>
            {user.mfaCodeRequired ? (
              <Flex direction="column" align="center" mb="2">
                <Heading onClick={onHeadingClick} align="center" mt="0" mb="1">
                  Multi-factor authentication
                </Heading>
                <Text size="2" color="gray" align="center">
                  {user.mfaType === 'email' ? (
                    <span>Enter the code from your email</span>
                  ) : (
                    <span>Enter the code from your authenticator app</span>
                  )}
                </Text>
              </Flex>
            ) : (
              heading && (
                <Heading onClick={onHeadingClick} align="center" mt="0">
                  {heading}
                </Heading>
              )
            )}
            {message && <Instruction text={message} />}
            <PlainLoginForm
              user={user}
              location={location}
              onSubmit={handleLogin}
              loginAttempts={loginAttempts} // Enable SAML either if in anonymous mode (no getBusiness passed in) or if SAML is enabled on the business
              samlEnabled={!!onSamlDiscovery || samlEnabled} // On SAML clicked, if anonymous business go to SAML discovery, otherwise go to main SAML
              onSamlClick={goToSaml}
              onForgotPasswordClick={() => setState('forgot_password')}
              signupUrl={signupUrl}
              signupCatalog={!!onSignupCatalogUser}
              onSignUpClick={() => setState('sign_up')}
              errorCode={errorCode}
              isTransitioning={isTransitioning}
            />
          </>
        ) : type === 'saml' ? (
          <>
            {heading && (
              <Heading onClick={onHeadingClick} align="center" mt="0">
                {heading}
              </Heading>
            )}
            <SamlForm location={location} onDismiss={() => setType('plain')} />
          </>
        ) : (
          onSamlDiscovery && (
            <>
              {heading && (
                <Heading onClick={onHeadingClick} align="center" mt="0">
                  {heading}
                </Heading>
              )}
              <SamlDiscoveryForm
                onSubmit={onSamlDiscovery}
                onDismiss={() => setType('plain')}
              />
            </>
          )
        )}
      </Flex>
    </Flex>
  )
}

export default LoginForm
